import React, { useEffect, useRef, useState } from "react";
import { StyleSheet, View, Image, Text, TouchableOpacity, PixelRatio } from "react-native";
import { Icons } from "../../assets";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import { useGlobalState } from "../../shared/state";
import { I18N } from "../../shared/util";
import TextTitle from "../../components/Text/TextTitle";
import useBirkenstockPrint from "./useBirkenstockPrint";
import BirkenstockLabel from "../../components/BirkenstockLabel";
import { useNavigation } from "@react-navigation/native";

type BasicMeasurements = {
  left: {
    length: number | undefined;
    width: number | undefined;
    widthCategory: number | undefined;
  };
  right: {
    length: number | undefined;
    width: number | undefined;
    widthCategory: number | undefined;
  };
};

const BirkenstockPrintScreen = () => {
  const [currentMeasurements] = useGlobalState("currentMeasurements");
  const [language] = useGlobalState("language");
  const [birkenstockSummary] = useGlobalState("birkenstockSummary");
  const [showHomeButton, setShowHomeButton] = useState<boolean>(false);

  const [basicMeasurement, setBasicMeasurement] = useState<BasicMeasurements>({
    left: { length: undefined, width: undefined, widthCategory: undefined },
    right: { length: undefined, width: undefined, widthCategory: undefined },
  });

  const i18n = I18N(language);
  const navigation = useNavigation();

  // convert MM TO CM
  const decimalAdjuster = (floatNumber: number) => parseFloat(((floatNumber * 10) / 100).toFixed(1));

  useEffect(() => {
    if (currentMeasurements) {
      Object.keys(currentMeasurements.measurements).map((measure: any) => {
        if (currentMeasurements.measurements[measure].side === "Left") {
          setBasicMeasurement((prevState: any) => ({
            ...prevState,
            left: {
              length: decimalAdjuster(currentMeasurements.measurements[measure].measurements.length_foot),
              width: decimalAdjuster(currentMeasurements.measurements[measure].measurements.width_ball),
              widthCategory: currentMeasurements.measurements[measure].measurements.width_category,
            },
          }));
        } else {
          setBasicMeasurement((prevState: any) => ({
            ...prevState,
            right: {
              length: decimalAdjuster(currentMeasurements.measurements[measure].measurements.length_foot),
              width: decimalAdjuster(currentMeasurements.measurements[measure].measurements.width_ball),
              widthCategory: currentMeasurements.measurements[measure].measurements.width_category,
            },
          }));
        }
      });
    }
  }, [currentMeasurements]);

  const printRef = useRef<HTMLDivElement>(null);

  const print = useBirkenstockPrint(printRef, () => {
    setShowHomeButton(true);
  });

  const handlePrint = () => {
    print();
  };

  return (
    <LayoutWrapper showHeaderCloseButton={false} showHeaderLogo={false} hideInfoButton={true} showBirkenstockFooterLogo={true} showBirkenstockFooter={true} showBirkenstockHomeButton={showHomeButton}>
      <View style={{ width: "85%", height: "100%", alignSelf: "center", justifyContent: "space-between", flexDirection: "row" }}>
        <View style={{ width: "60%", height: "100%", justifyContent: "space-around", alignItems: "center", flexDirection: "row" }}>
          <View
            style={{
              width: "70%",
              height: "100%",
              maxWidth: 450,
              flexDirection: "column",
              justifyContent: "space-around",
              backgroundColor: "white",
              shadowColor: "#000",
              shadowOffset: { width: 6 / PixelRatio.get(), height: 6 / PixelRatio.get() },
              shadowOpacity: 0.16,
              shadowRadius: 4 / PixelRatio.get(),
              elevation: 1,
              paddingVertical: "1%",
            }}
          >
            <Image source={Icons.progressIconBirkenstock} style={{ width: "30%", height: "25%", resizeMode: "contain", alignSelf: "center" }} />
            <View style={{ width: "80%", height: "20%", justifyContent: "space-between", alignItems: "flex-start", flexDirection: "row", alignSelf: "center" }}>
              <Image source={Icons.sandalRecommendationIcon} style={{ width: "35%", height: "85%", resizeMode: "contain", alignSelf: "center" }} />
              <Image source={Icons.shoeRecommendationIcon} style={{ width: "35%", height: "85%", resizeMode: "contain", alignSelf: "center" }} />
            </View>
            <View style={{ width: "90%", height: "10%", justifyContent: "space-between", alignItems: "flex-start", flexDirection: "row", alignSelf: "center" }}>
              <View style={{ alignItems: "center", width: "33%", alignContent: "center", borderBottomWidth: 2, borderColor: "#0e2288" }}>
                <TextTitle text={birkenstockSummary && birkenstockSummary.sandal.size} fontSize={36} color="#0e2288" fontFamily="Geist-Light" />
              </View>
              <TextTitle text={i18n.t("shoeSize")} fontSize={40} color="#0e2288" fontFamily="Birkentype" />
              <View style={{ alignItems: "center", width: "33%", alignContent: "center", borderBottomWidth: 2, borderColor: "#0e2288" }}>
                <TextTitle text={birkenstockSummary && birkenstockSummary.shoe.size} fontSize={36} color="#0e2288" fontFamily="Geist-Light" />
              </View>
            </View>
            {/* CHECK BOXES START*/}
            <View style={{ width: "90%", height: "10%", justifyContent: "space-between", flexDirection: "row", alignSelf: "center" }}>
              {/* LEFT */}
              <View style={{ width: "80%", justifyContent: "space-around" }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <View style={[styles.checkbox, styles.checkboxChecked]}>
                    <Text style={styles.checkboxX}>{birkenstockSummary && birkenstockSummary.sandal.width.toLocaleLowerCase() === "regular" && "X"}</Text>
                  </View>
                  <Text style={styles.checkboxLabel}>regular</Text>
                </View>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <View style={[styles.checkbox, styles.checkboxChecked]}>
                    <Text style={styles.checkboxX}>{birkenstockSummary && birkenstockSummary.sandal.width.toLocaleLowerCase() === "narrow" && "X"}</Text>
                  </View>
                  <Text style={styles.checkboxLabel}>narrow</Text>
                </View>
              </View>

              {/* RIGHT */}
              <View style={{ width: "40%", justifyContent: "space-around" }}>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <View style={[styles.checkbox, styles.checkboxChecked]}>
                    <Text style={styles.checkboxX}>{birkenstockSummary && birkenstockSummary.sandal.width.toLocaleLowerCase() === "regular" && "X"}</Text>
                  </View>
                  <Text style={styles.checkboxLabel}>regular</Text>
                </View>
                <View style={{ flexDirection: "row", alignItems: "center" }}>
                  <View style={[styles.checkbox, styles.checkboxChecked]}>
                    <Text style={styles.checkboxX}>{birkenstockSummary && birkenstockSummary.sandal.width.toLocaleLowerCase() === "narrow" && "X"}</Text>
                  </View>
                  <Text style={styles.checkboxLabel}>narrow</Text>
                </View>
              </View>
            </View>
            {/* CHECK BOXES END*/}

            {/* SIZES START*/}
            <View style={{ width: "40%", flexDirection: "row", justifyContent: "space-between", alignSelf: "flex-end", marginRight: "12%" }}>
              <Text style={styles.lrLabel}>L</Text>
              <Text style={styles.lrLabel}>R</Text>
            </View>

            <View style={{ width: "85%", alignSelf: "center" }}>
              <View style={styles.measureRow}>
                <Text style={styles.measureLabel}>{i18n.t("MeasurementScale.length")}</Text>
                {/* Left */}
                <Text style={styles.measureValue}>{basicMeasurement && basicMeasurement?.left.length}</Text>
                {/* Right */}
                <Text style={styles.measureValue}>{basicMeasurement && basicMeasurement?.right.length}</Text>
              </View>

              <View style={styles.measureRow}>
                <Text style={styles.measureLabel}>{i18n.t("MeasurementScale.width")}</Text>
                {/* Left */}
                <Text style={styles.measureValue}>{basicMeasurement && basicMeasurement?.left.width}</Text>
                {/* Right */}
                <Text style={styles.measureValue}>{basicMeasurement && basicMeasurement?.right.width}</Text>
              </View>
            </View>
            {/* SIZES END*/}

            <View style={{ width: "80%", height: "10%", alignItems: "center", flexDirection: "row", justifyContent: "space-between", alignSelf: "center" }}>
              <TextTitle text="SCAN ID:" fontSize={30} color="#0e2288" fontFamily="Geist-Light" />
              <Text style={styles.scanValue}>{birkenstockSummary && birkenstockSummary.scanId}</Text>
            </View>
          </View>
        </View>
        <View style={{ width: "40%", height: "100%", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
          <TouchableOpacity
            onPress={() => handlePrint()}
            style={{
              width: "80%",
              height: "25%",
              padding: "5%",
              borderWidth: 2,
              borderRadius: 12,
              borderColor: "#0e2288",
              backgroundColor: "#0e2288",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Text style={styles.scanLabel}>{i18n.t("printButton")}</Text>
          </TouchableOpacity>
          <View pointerEvents="none" style={styles.printBuffer}>
            {/* Hidden in UI – used only for printing */}
            <div id="print-birkenstock-label" ref={printRef} style={{ position: "absolute", top: -9999, left: -9999 }}>
              {birkenstockSummary && <BirkenstockLabel summary={birkenstockSummary} />}
            </div>
          </View>
        </View>
      </View>
    </LayoutWrapper>
  );
};

export default BirkenstockPrintScreen;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "row",
  },
  checkbox: {
    width: 22,
    height: 22,
    borderWidth: 2,
    borderColor: "#0e2288",
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  checkboxChecked: {
    backgroundColor: "white",
  },
  checkboxX: {
    color: "#0e2288",
    fontSize: 20,
    fontWeight: "bold",
  },
  checkboxLabel: {
    fontFamily: "Geist-Light",
    color: "#0e2288",
    fontSize: 20,
  },
  lrLabel: {
    color: "#0e2288",
    fontSize: 24,
    fontWeight: "bold",
  },
  measureRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginVertical: 6,
  },
  measureLabel: {
    fontFamily: "Geist-Light",
    color: "#0e2288",
    fontSize: 20,
  },
  measureValue: {
    fontFamily: "Geist-Light",
    color: "#000",
    fontSize: 24,
    borderBottomWidth: 2,
    borderColor: "#0e2288",
    width: 60,
    textAlign: "center",
  },
  scanLabel: {
    fontFamily: "Birkentype",
    fontSize: 50,
    color: "white",
    fontWeight: "900",
    textAlign: "center",
  },
  scanValue: {
    fontFamily: "Geist-Light",
    color: "#000",
    fontSize: 26,
    borderBottomWidth: 2,
    borderColor: "#0e2288",
    paddingHorizontal: 10,
  },
  printBuffer: {
    width: 0,
    height: 0,
    overflow: "hidden",
  },
});
